import React, { useEffect, useState } from "react";
import "../styles/DonationModal.css";
import { IoMdClose } from "react-icons/io";
import { IoIosClose } from "react-icons/io";
import DonationForm from "./DonationForm";
import {
  Form,
  Row,
  Col,
  Button,
  Spinner,
  FloatingLabel,
} from "react-bootstrap";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import PaymentElementForm from "./PaymentForm.jsx";
import { API_BASE } from "../config";

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
);

const isAlphabetic = (v) => /^[a-zA-Z\s\-']*$/.test(v);
const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const isNumeric = (v) => /^[0-9]*$/.test(v);

const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut",
  "Delaware","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa",
  "Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan",
  "Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada",
  "New Hampshire","New Jersey","New Mexico","New York","North Carolina",
  "North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania","Rhode Island",
  "South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont",
  "Virginia","Washington","West Virginia","Wisconsin","Wyoming",
];

export default function DonationModal({
  open,
  onClose,
  setDonationAmount,
  donationAmount,
  setDedicatedTo,
  onDonationSaved,
  startOnContact = false,
}) {
  const [showContactFlow, setShowContactFlow] = useState(startOnContact);
  const [step, setStep] = useState(1);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [street, setStreet] = useState("");
  const [aptSuite, setAptSuite] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [country, setCountry] = useState("United States");
  const [clientSecret, setClientSecret] = useState("");
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [errors, setErrors] = useState({});
  const [infoTab, setInfoTab] = useState("secure");
  const [showInfoBubble, setShowInfoBubble] = useState(false);

  // NEW: donation preferences
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [canContact, setCanContact] = useState(false);

  const isUS = country === "United States";

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      setShowContactFlow(startOnContact);
    }
  }, [open, startOnContact]);

  useEffect(() => {
    if (!open) {
      setShowContactFlow(false);
      setStep(1);
      setFirstName("");
      setLastName("");
      setEmail("");
      setPhone("");
      setStreet("");
      setAptSuite("");
      setCity("");
      setState("");
      setZip("");
      setCountry("United States");
      setClientSecret("");
      setErrors({});
      setInfoTab("secure");
      setShowInfoBubble(false);
      setIsAnonymous(false);
      setCanContact(false);
    }
  }, [open]);

  useEffect(() => {
    if (!isUS) {
      setState("");
      setErrors((prev) => ({ ...prev, state: "" }));
    }
  }, [isUS]);

  const validateField = (field) => {
    let msg = "";
    switch (field) {
      case "firstName":
        if (!firstName.trim()) msg = "First name is required.";
        break;
      case "lastName":
        if (!lastName.trim()) msg = "Last name is required.";
        break;
      case "email":
        if (!email.trim() || !isValidEmail(email))
          msg = "Valid email is required. (joedoe@example.com)";
        break;
      case "phone": {
        const digits = phone.replace(/\D/g, "");
        if (digits && digits.length < 10) msg = "Phone must be 10 digits.";
        break;
      }
      case "street":
        if (!street.trim()) msg = "Street is required.";
        break;
      case "city":
        if (!city.trim()) msg = "City is required.";
        else if (!isAlphabetic(city)) msg = "City must contain only letters.";
        break;
      case "state":
        if (isUS && !state.trim()) msg = "State is required.";
        break;
      case "zip": {
        const digits = zip.replace(/\D/g, "");
        if (digits.length !== 5) msg = "ZIP must be 5 digits.";
        break;
      }
      case "country":
        if (!country.trim()) msg = "Country is required.";
        break;
      default:
        break;
    }
    setErrors((prev) => ({ ...prev, [field]: msg }));
    return msg === "";
  };

  const formatPhone = (v) => {
    const d = v.replace(/\D/g, "").slice(0, 10);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
    return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  };

  const handleNext = async () => {
    if (step === 1) {
      const fields = ["firstName", "lastName", "email", "phone"];
      const ok = fields.every((f) => validateField(f));
      if (!ok) return;
      setStep(2);
    } else if (step === 2) {
      const fields = ["street", "city", "zip", "country"];
      if (isUS) {
        fields.push("state");
      }
      const ok = fields.every((f) => validateField(f));
      if (!ok) return;

      setLoadingPayment(true);
      try {
        const res = await fetch(`${API_BASE}/api/Payments/create-intent`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ amount: donationAmount }),
        });
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        setClientSecret(data.clientSecret);
        setStep(3);
      } catch (err) {
        console.error(err);
        alert("Failed to create payment intent.");
      } finally {
        setLoadingPayment(false);
      }
    }
  };

  const handlePaymentSuccess = (paymentIntent) => {
    onDonationSaved?.({
      email: email.trim(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      phone: phone.trim(),
      street: street.trim(),
      aptSuite: aptSuite.trim() || null,
      city: city.trim(),
      state: isUS ? state.trim() : null,
      zip: zip.trim(),
      country: country.trim(),
      amountCents: donationAmount * 100,
      currency: "usd",
      paymentIntentId: paymentIntent.id,
      paymentStatus: 1,
      paymentMethod: paymentIntent.payment_method,
      isAnonymous,
      canContact,
    });

    onClose();
  };

  const handleOverlayClick = () => {
    if (showInfoBubble) {
      setShowInfoBubble(false);
    }
  };

  const handleModalClick = (e) => {
    e.stopPropagation();
    if (showInfoBubble) {
      setShowInfoBubble(false);
    }
  };

  if (!open) return null;

  return (
    <div className="donation-overlay" onClick={handleOverlayClick}>
      <div className="donation-modal" onClick={handleModalClick}>
        <button className="donation-close" onClick={onClose}>
          <IoMdClose />
        </button>

        <div className="donation-left">
          <img
            src="/img/communion.JPG"
            alt="Gail Thompson Foundation"
            className="donation-hero"
          />
          <div className="donation-left-content">
            <h2>Your gift carries Gail’s legacy forward</h2>
            <p>
              This campaign was created in honor of Gail “Tee” Thompson, a mom and grandmother whose kindness and
              fight through Alzheimer’s inspired us to do something for families like hers. The Bowdoin Baseball team
              is rallying together — players, alumni, parents, and friends — to turn Cal, a junior on the team, and his
              family’s story into real support for people living with Alzheimer’s in Maine.
            </p>
            <p className="donation-left-thanks">
              Thank you for helping us keep Gail’s legacy alive
            </p>
          </div>
        </div>

        <div className="donation-right">
          {!showContactFlow ? (
            <DonationForm
              setDonationAmount={setDonationAmount}
              donationAmount={donationAmount}
              setDedicatedTo={setDedicatedTo}
              onProceedToContact={() => {
                setShowContactFlow(true);
                setStep(1);
              }}
            />
          ) : (
            <div className="contact-flow-panel">
              <div className="fw-semibold mb-2">
                Donating: ${Number(donationAmount || 0).toFixed(2)}
              </div>

              <h3 className="mb-3">
                {step === 1
                  ? "Enter Your Information"
                  : step === 2
                  ? "Enter Your Address"
                  : "Complete Your Donation"}
              </h3>

              {step === 1 && (
                <Form noValidate>
                  <FloatingLabel controlId="firstName" label="First Name" className="mb-3">
                    <Form.Control
                      placeholder="First Name"
                      size="lg"
                      value={firstName}
                      isInvalid={!!errors.firstName}
                      onBlur={() => validateField("firstName")}
                      onChange={(e) =>
                        isAlphabetic(e.target.value) &&
                        setFirstName(e.target.value)
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.firstName}
                    </Form.Control.Feedback>
                  </FloatingLabel>

                  <FloatingLabel controlId="lastName" label="Last Name" className="mb-3">
                    <Form.Control
                      placeholder="Last Name"
                      size="lg"
                      value={lastName}
                      isInvalid={!!errors.lastName}
                      onBlur={() => validateField("lastName")}
                      onChange={(e) =>
                        isAlphabetic(e.target.value) &&
                        setLastName(e.target.value)
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.lastName}
                    </Form.Control.Feedback>
                  </FloatingLabel>

                  <FloatingLabel controlId="email" label="Email Address" className="mb-3">
                    <Form.Control
                      type="email"
                      placeholder="you@example.com"
                      size="lg"
                      value={email}
                      isInvalid={!!errors.email}
                      onBlur={() => validateField("email")}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.email}
                    </Form.Control.Feedback>
                  </FloatingLabel>

                  <FloatingLabel controlId="phone" label="Phone (Optional)" className="mb-3">
                    <Form.Control
                      placeholder="123-456-7890"
                      size="lg"
                      value={phone}
                      isInvalid={!!errors.phone}
                      onBlur={() => validateField("phone")}
                      onChange={(e) => setPhone(formatPhone(e.target.value))}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.phone}
                    </Form.Control.Feedback>
                  </FloatingLabel>

                  <div className="mb-2">
                    <Form.Check
                      id="isAnonymous"
                      type="checkbox"
                      className="mb-2"
                      checked={isAnonymous}
                      onChange={(e) => setIsAnonymous(e.target.checked)}
                      label="Make this donation anonymous (your name won't be shown publicly)."
                    />
                    <Form.Check
                      id="canContact"
                      type="checkbox"
                      className="mb-1"
                      checked={canContact}
                      onChange={(e) => setCanContact(e.target.checked)}
                      label="I agree to receive occasional updates from the Gail Thompson Foundation."
                    />
                    <div className="small text-muted">
                      We&apos;ll still email your receipt even if you keep updates turned off.
                    </div>
                  </div>
                </Form>
              )}

              {step === 2 && (
                <Form noValidate>
                  <FloatingLabel controlId="street" label="Street Address" className="mb-3">
                    <Form.Control
                      placeholder="123 Main St"
                      size="lg"
                      value={street}
                      isInvalid={!!errors.street}
                      onBlur={() => validateField("street")}
                      onChange={(e) => setStreet(e.target.value)}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.street}
                    </Form.Control.Feedback>
                  </FloatingLabel>

                  <FloatingLabel controlId="aptSuite" label="Apt / Suite (Optional)" className="mb-3">
                    <Form.Control
                      placeholder="Apt 4B"
                      size="lg"
                      value={aptSuite}
                      onChange={(e) => setAptSuite(e.target.value)}
                    />
                  </FloatingLabel>

                  <FloatingLabel controlId="city" label="City" className="mb-3">
                    <Form.Control
                      placeholder="City"
                      size="lg"
                      value={city}
                      isInvalid={!!errors.city}
                      onBlur={() => validateField("city")}
                      onChange={(e) =>
                        isAlphabetic(e.target.value) &&
                        setCity(e.target.value)
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.city}
                    </Form.Control.Feedback>
                  </FloatingLabel>

                  <Row>
                    <Col md={6}>
                      <FloatingLabel
                        controlId="state"
                        label={isUS ? "State" : "State (not required)"}
                        className="mb-3"
                      >
                        {isUS ? (
                          <Form.Select
                            size="lg"
                            value={state}
                            isInvalid={!!errors.state}
                            onBlur={() => validateField("state")}
                            onChange={(e) => setState(e.target.value)}
                          >
                            <option value="">Choose a state</option>
                            {US_STATES.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </Form.Select>
                        ) : (
                          <Form.Control size="lg" value="" disabled />
                        )}
                        <Form.Control.Feedback type="invalid">
                          {errors.state}
                        </Form.Control.Feedback>
                      </FloatingLabel>
                    </Col>

                    <Col md={6}>
                      <FloatingLabel controlId="zip" label="ZIP Code" className="mb-3">
                        <Form.Control
                          placeholder="ZIP"
                          size="lg"
                          value={zip}
                          isInvalid={!!errors.zip}
                          onBlur={() => validateField("zip")}
                          inputMode="numeric"
                          maxLength={5}
                          pattern="\\d{5}"
                          onChange={(e) => {
                            const v = e.target.value.replace(/\D/g, "").slice(0, 5);
                            setZip(v);
                          }}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.zip}
                        </Form.Control.Feedback>
                      </FloatingLabel>
                    </Col>
                  </Row>

                  <FloatingLabel controlId="country" label="Country" className="mb-3">
                    <Form.Select
                      size="lg"
                      value={country}
                      isInvalid={!!errors.country}
                      onBlur={() => validateField("country")}
                      onChange={(e) => setCountry(e.target.value)}
                    >
                      <option value="">Select a country</option>
                      <option>United States</option>
                      <option>Canada</option>
                      <option>United Kingdom</option>
                      <option>Australia</option>
                      <option>Other</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.country}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Form>
              )}

              {step === 3 && loadingPayment && (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                  <div className="mt-3">Preparing secure payment...</div>
                </div>
              )}

              {step === 3 && clientSecret && (
                <Elements stripe={stripePromise} options={{ clientSecret }}>
                  <PaymentElementForm
                    amount={donationAmount}
                    clientSecret={clientSecret}
                    onSuccess={handlePaymentSuccess}
                    onBack={() => setStep(2)}
                  />
                </Elements>
              )}

              {step < 3 && (
                <div className="d-flex justify-content-between mt-3">
                  {step === 1 ? (
                    <Button
                      variant="outline-secondary"
                      onClick={() => {
                        setShowContactFlow(false);
                        setStep(1);
                      }}
                    >
                      Back
                    </Button>
                  ) : (
                    <Button
                      variant="outline-secondary"
                      onClick={() => setStep(step - 1)}
                    >
                      Back
                    </Button>
                  )}

                  <Button variant="primary" size="lg" onClick={handleNext}>
                    {step === 2 && loadingPayment ? (
                      <>
                        <Spinner size="sm" animation="border" /> Loading...
                      </>
                    ) : (
                      "Next"
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        <div
          className="donation-bottom-info"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className={
              infoTab === "secure"
                ? "donation-bottom-pill active"
                : "donation-bottom-pill"
            }
            onClick={() => {
              setInfoTab("secure");
              setShowInfoBubble(true);
            }}
          >
            Is my donation secure?
          </button>
          <span className="mx-2 text-muted">·</span>
          <button
            className={
              infoTab === "tax"
                ? "donation-bottom-pill active"
                : "donation-bottom-pill"
            }
            onClick={() => {
              setInfoTab("tax");
              setShowInfoBubble(true);
            }}
          >
            Is this donation tax-deductible?
          </button>

          {showInfoBubble && (
            <div
              className="donation-bottom-bubble"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="donation-bottom-bubble-head">
                <span>
                  {infoTab === "secure"
                    ? "Is my donation secure?"
                    : "Is this donation tax-deductible?"}
                </span>
                <button
                  className="donation-bottom-icon-close"
                  onClick={() => setShowInfoBubble(false)}
                >
                  <IoIosClose size={20} />
                </button>
              </div>
              <div className="donation-bottom-bubble-body">
                {infoTab === "secure" ? (
                  <p className="mb-0">
                    Your payment is processed through Stripe, a leading global payments platform used by millions of businesses. All transactions are encrypted over HTTPS (SSL), and full card details never touch our servers. They go directly to Stripe’s PCI-DSS–compliant infrastructure for secure authorization and storage. We only keep the minimum donor information needed for your receipt and impact reporting, and we do not sell or share your payment data.
                  </p>
                ) : (
                  <p className="mb-0">
                    Yes. Gifts to the Gail Thompson Foundation, a recognized 501(c)(3) nonprofit organization, are generally tax-deductible to the extent allowed by U.S. law. After you donate, we’ll email you a receipt containing the donation amount and our EIN for your records. Please keep this receipt for your tax filings. Your individual deductibility can vary based on your personal circumstances, so we always recommend consulting your tax advisor or accountant.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
